import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";

import {
  WorkflowLedger,
  WorkflowLedgerOwner,
  WorkflowLedgerItem,
  WorkflowLedgerStep,
  WorkflowLedgerViewState,
  workflowLedgerStatusTone,
  workflowLedgerWaitingLabels,
} from "./workflow-ledger.models";

let nextSummaryId = 0;

@Component({
  selector: "phms-workflow-summary-bar",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./workflow-summary-bar.component.html",
  styleUrl: "./workflow-summary-bar.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowSummaryBarComponent {
  @Input() ledger: WorkflowLedger | null = null;
  @Input() state: WorkflowLedgerViewState = "ready";
  @Input() errorMessage = "Workflow context could not be loaded.";
  @Input() expanded = false;
  @Input() controlsId: string | null = null;
  @Input() showToggle = true;
  /** Dense masthead-adjacent presentation; detailed history remains available on demand. */
  @Input() compact = false;

  @Output() readonly expandedChange = new EventEmitter<boolean>();

  protected get currentStep(): WorkflowLedgerStep | undefined {
    return this.ledger?.steps.find(step => step.isCurrent)
      ?? this.ledger?.steps.find(step => step.stepCode === this.ledger?.currentStepCode);
  }

  protected get currentStepLabel(): string {
    return this.ledger?.currentStepLabel?.trim()
      || this.ledger?.steps.find((step) => step.isCurrent)?.stepLabel
      || "No current step";
  }

  private get currentStepOwner(): WorkflowLedgerOwner | null | undefined {
    return this.currentStep?.owner ?? this.ledger?.currentOwner;
  }

  protected get isUnassigned(): boolean {
    return !actorLabel(this.currentStepOwner?.assignee);
  }

  protected get stageStartedUtc(): string | null {
    const step = this.currentStep;
    return step?.isCurrent && !step.completedUtc ? step.startedUtc ?? null : null;
  }

  protected get pendingDecisions(): WorkflowLedgerItem[] {
    const entries = [...(this.ledger?.steps.flatMap(step => step.items) ?? []), ...(this.ledger?.workflowItems ?? [])];
    const unique = new Map<string, WorkflowLedgerItem>();
    for (const item of entries) {
      if (item.kind === "decision" && item.posture === "open"
        && (!item.statusCode || item.statusCode === "pending")) {
        unique.set(String(item.decisionRecordId ?? item.id), item);
      }
    }
    return [...unique.values()].sort((a, b) => dateOrder(a.dueUtc) - dateOrder(b.dueUtc));
  }

  protected get attentionLabel(): string {
    const count = this.pendingDecisions.length;
    if (count) return `${count} pending ${count === 1 ? "decision" : "decisions"}`;
    const attention = this.ledger?.counts.attentionSteps ?? 0;
    return attention ? `${attention} ${attention === 1 ? "step needs" : "steps need"} attention` : "Workflow evidence";
  }

  protected get attentionDetail(): string {
    const pending = this.pendingDecisions[0];
    if (pending) return pending.decisionPurposeLabel?.trim() || pending.decisionTypeLabel?.trim() || pending.label;
    const step = this.ledger?.steps.find(step => workflowLedgerStatusTone(step.statusCode) === "attention");
    if (step) return `${step.stepLabel}: ${step.statusLabel}`;
    return this.ledger?.evidenceAvailability === "available" ? "No pending decisions recorded" : "Some workflow evidence is unavailable";
  }

  protected get attentionTitle(): string {
    return this.pendingDecisions.map(item => item.decisionPurposeLabel || item.decisionTypeLabel || item.label).join("; ") || this.attentionDetail;
  }

  protected get dueUtc(): string | null {
    const due = this.pendingDecisions[0]?.dueUtc;
    return due && Number.isFinite(Date.parse(due)) ? due : null;
  }

  protected get hasAttention(): boolean {
    return this.pendingDecisions.length > 0 || (this.ledger?.counts.attentionSteps ?? 0) > 0;
  }

  protected get isOverdue(): boolean {
    const due = this.dueUtc;
    return due !== null && Date.parse(due) < Date.now();
  }

  protected openDetails(): void { this.expandedChange.emit(true); }

  protected shortDate(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric", year: "numeric",
    }).format(date);
  }

  protected get currentOwnerLabel(): string {
    return actorLabel(this.currentStepOwner?.assignee) || "Unassigned";
  }

  protected get currentQueueLabel(): string {
    const owner = this.currentStepOwner;
    return owner?.queueLabel?.trim() || owner?.queueCode?.trim() || "";
  }

  protected readonly detailsId = `workflow-summary-details-${++nextSummaryId}`;

  protected get disclosureControls(): string {
    return [this.detailsId, this.controlsId].filter(Boolean).join(" ");
  }

  protected get progressLabel(): string {
    const counts = this.ledger?.counts;
    if (!counts || counts.totalSteps === 0) {
      return "No workflow steps recorded";
    }

    const parts = [`${counts.completedSteps} of ${counts.totalSteps} steps complete`];
    if ((counts.bypassedSteps ?? 0) > 0) {
      parts.push(`${counts.bypassedSteps} bypassed`);
    }
    if (counts.pendingSteps !== undefined || counts.assignedSteps !== undefined) {
      parts.push(...workflowLedgerWaitingLabels(counts).filter(label => !label.startsWith("0 ")));
    }
    if (counts.activeSteps > 1) {
      parts.push(`${counts.activeSteps} active`);
    }
    if (counts.attentionSteps > 0) {
      parts.push(`${counts.attentionSteps} ${counts.attentionSteps === 1 ? "needs" : "need"} attention`);
    }
    return parts.join(" · ");
  }

  protected get progressAriaLabel(): string {
    const counts = this.ledger?.counts;
    if (!counts || counts.totalSteps === 0) {
      return this.progressLabel;
    }
    const labels = [
      `${counts.completedSteps} complete`,
      `${counts.activeSteps} active`,
      ...workflowLedgerWaitingLabels(counts),
    ];
    if ((counts.bypassedSteps ?? 0) > 0) labels.push(`${counts.bypassedSteps} bypassed`);
    if (counts.attentionSteps > 0) labels.push(`${counts.attentionSteps} need attention`);
    return labels.join(", ");
  }

  protected get stateMessage(): string {
    switch (this.state) {
      case "loading":
        return "Loading workflow context...";
      case "unavailable":
        return "Workflow context is unavailable.";
      case "error":
        return this.errorMessage;
      default:
        return "Workflow context is not available.";
    }
  }

  protected get statusTone(): string {
    return workflowLedgerStatusTone(this.ledger?.statusCode);
  }

  protected get toggleLabel(): string {
    return this.expanded ? "Hide workflow details and history" : "View workflow details and history";
  }

  protected get politeAnnouncement(): string {
    if (this.state !== "ready" || !this.ledger) {
      return "";
    }

    return `${this.ledger.workflowLabel}. ${this.ledger.statusLabel}. ${this.currentStepLabel}. ${this.progressLabel}.`;
  }

  protected toggleExpanded(): void {
    this.expandedChange.emit(!this.expanded);
  }

  protected formatDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }
}

function dateOrder(value: string | null | undefined): number {
  const date = value ? Date.parse(value) : NaN;
  return Number.isFinite(date) ? date : Number.MAX_SAFE_INTEGER;
}

function actorLabel(actor: WorkflowLedgerOwner["assignee"]): string | null {
  const displayName = actor?.displayName?.trim();
  if (displayName) {
    return displayName;
  }
  if (actor?.actorType === "system") {
    return "System";
  }
  return actor?.userId == null ? null : `User #${actor.userId}`;
}
